import classNames from 'classnames';
import { paddingMinusBorder } from '../../utils/tailwind/paddingMinusBorder';
import type {
  SegmentButtonPosition,
  SegmentButtonSize,
  SegmentButtonVariants,
} from './SegmentButton.types';

const baseInteractivityClassName = [
  'cursor-pointer',
  'focus-visible:outline-none',
  'focus-visible:ring',
  'hover:bg-state-hover',
  'active:bg-state-press',
  'focus:z-20',
];

const enabledBaseVariantClassName: {
  [Key in SegmentButtonVariants]: string[];
} = {
  outline: [
    ...baseInteractivityClassName,
    'border',
    'border-default',
    'border-solid',
    'bg-default',
    'active:border-status-pressed',
  ],
  plain: [
    ...baseInteractivityClassName,
    'border-none',
    'bg-transparent',
    'active:border-status-pressed',
  ],
};

const enabledVariantClassName: {
  [VariantKey in SegmentButtonVariants]: string[];
} = {
  outline: enabledBaseVariantClassName.outline,
  plain: enabledBaseVariantClassName.plain,
};

const notFirstClassName = 'rounded-tl-none rounded-bl-none';
const notLastClassName = 'rounded-tr-none rounded-br-none';

const baseDisabledClassName = [
  'pointer-events-none',
  'text-status-disabled-dark',
];
const disabledVariantClassName: { [Key in SegmentButtonVariants]: string[] } = {
  outline: [
    ...baseDisabledClassName,
    'bg-status-disabled',
    'border',
    'border-status-disabled-dark',
    'border-solid',
    'text-status-disabled',
  ],
  plain: [...baseDisabledClassName, 'border-none'],
};

const selectedClassName: (disabled?: boolean) => {
  [Key in SegmentButtonVariants]: string;
} = (disabled) => {
  if (disabled) {
    return {
      outline: 'bg-status-disabled-dark z-elevated',
      plain: 'bg-status-disabled z-elevated',
    };
  }
  return {
    outline:
      'bg-status-selected text-status-selected border-status-selected z-elevated',
    plain: 'bg-transparent text-status-selected z-elevated',
  };
};

const sizeClassName: { [Key in SegmentButtonSize]: string } = {
  small: `${paddingMinusBorder['2xs']} text-xs`,
  default: `${paddingMinusBorder['2xs']} text-sm`,
  large: `${paddingMinusBorder.xs} text-base`,
};

const radiusClassName: { [Key in SegmentButtonSize]: string } = {
  small: 'rounded-sm',
  default: 'rounded',
  large: 'rounded-lg',
};

export const getSegmentButtonClassNames = (
  className: string | undefined,
  variant: SegmentButtonVariants,
  size: SegmentButtonSize,
  position: SegmentButtonPosition,
  selected: boolean,
  disabled: boolean,
): string =>
  classNames(
    className,
    [
      'relative',
      'inline-flex',
      'shrink-0',
      'items-center',
      'justify-center',
      'whitespace-nowrap',
      'no-underline',
      'text-default',
      'subtract-border',
      !position.first && 'ml-[-1px]',
    ],
    disabled
      ? disabledVariantClassName[variant].join(' ')
      : enabledVariantClassName[variant].join(' '),
    selected && selectedClassName(disabled)[variant],
    sizeClassName[size],
    radiusClassName[size],
    !position.first && notFirstClassName,
    !position.last && notLastClassName,
  );
